import React, { useEffect, useState } from 'react'
import { Link, Redirect } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'

import { toast } from 'react-toastify'
import { Helmet } from 'react-helmet'

import { Heading, NotFoundMessage, UnauthorisedMessage } from '@peracto/peracto-ui'
import { GET_ONE, UPDATE, useClient, getSchemaFromResource } from '@peracto/client'

const AttributeSetEditContainer = ({ children }) => {
    return (
        <div className="form-container">
            <Heading name="Edit Attribute Set">
                <div className="!grow !flex !items-center !justify-end">
                    <Link
                        className="btn btn-outline-primary"
                        to="/attribute-sets"
                        data-testid="back-to-attribute-sets"
                    >
                        <FontAwesomeIcon icon={faArrowCircleLeft} className="!mr-2" />
                        Back to Attribute Sets
                    </Link>
                </div>
            </Heading>
            {children}
        </div>
    )
}

const AttributeSetEdit = ({ AttributeSetForm, location: { pathname } }) => {
    const { client, getResource } = useClient()
    const [loading, setLoading] = useState(true)
    const [unauthorised, setUnauthorised] = useState(false)
    const [notFound, setNotFound] = useState(false)
    const [redirect, setRedirect] = useState()

    const [attributeSet, setAttributeSet] = useState()
    const [groups, setGroups] = useState()

    const fetchAttribute = async () => {
        try {
            const { data, response } = await client(GET_ONE, 'attribute-sets', {
                id: pathname,
            })

            setAttributeSet(data)
            setGroups(response.json.groups)
            setLoading(false)

            if (response.status === 404) {
                setRedirect('/attribute-sets')
            }
        } catch (e) {
            console.error(e)

            if (e.status === 403) {
                setUnauthorised(true)
            }

            if (e.status === 404) {
                setNotFound(true)
            }

            setLoading(false)
            setRedirect('/attribute-sets')
        }
    }

    useEffect(() => {
        fetchAttribute()
    }, [])

    const schema = getSchemaFromResource(getResource('attribute-sets'))

    const onSubmit = async (data, actions) => {
        try {
            const formData = {
                ...data,
                groups: data.groups.map(attr => attr['@id']),
            }

            const response = await client(UPDATE, 'attribute-sets', {
                id: pathname,
                data: formData,
            })

            if (response.data.violations && response.data.violations.length > 0) {
                // Display errors for invalid fields
                actions.setSubmitting(false)
                response.data.violations.map(error => {
                    actions.setFieldError(error.propertyPath, error.message)
                })
            } else {
                actions.setSubmitting(false)
                toast.success('Attribute Set successfully updated!')
            }
        } catch (e) {
            console.error(e)
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
            if (e?.error?.body?.violations?.length > 0) {
                // Display errors for invalid fields
                e.error.body.violations.map(error => {
                    actions.setFieldError(error.propertyPath, error.message)
                })
            }
            actions.setSubmitting(false)
        }
    }

    if (loading) {
        return (
            <AttributeSetEditContainer>
                <div className="card">
                    <div className="card-body">Loading...</div>
                </div>
            </AttributeSetEditContainer>
        )
    }

    if (unauthorised) {
        return <UnauthorisedMessage />
    }

    if (notFound) {
        return (
            <NotFoundMessage
                url="/attribute-sets"
                message="The attribute set you're looking for could not be found"
                buttonLabel="Go to Attribute Sets"
            />
        )
    }

    return (
        <AttributeSetEditContainer>
            {redirect && <Redirect to={redirect} />}

            <Helmet>
                <title>{attributeSet.name || 'Attribute Set'} | Edit | Peracto</title>
            </Helmet>

            <AttributeSetForm
                values={attributeSet}
                attributeSetGroups={groups}
                onSubmit={onSubmit}
                schema={schema}
            />
        </AttributeSetEditContainer>
    )
}

export default AttributeSetEdit
